import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, Link2, Image as ImageIcon, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { renderScorecard } from "../lib/scorecard";
import { SITE_URL, canonicalFor } from "../lib/seo";
import { slugify } from "../lib/api";

export const ShareMenu = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const url = canonicalFor(`/product/${data.slug || slugify(data.productName)}`);
  const text = `I just checked ${data.productName} on WhatIsInside — scored ${data.overallScore}/100. Here's what's really inside it`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const shareLink = async () => {
    setOpen(false);
    if (navigator.share) {
      try {
        await navigator.share({ title: `${data.productName} — WhatIsInside`, text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const shareImage = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const blob = await renderScorecard(data, SITE_URL);
      const file = new File([blob], `${slugify(data.productName)}-whatisinside.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `${data.productName} — WhatIsInside`, text });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("Scorecard image downloaded");
    } catch (e) {
      console.error("[share] image failed", e);
      toast.error("Couldn't create the scorecard image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="lime-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-2"
        data-testid="share-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
        Share
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            data-testid="share-menu"
            className="absolute right-0 mt-2 w-56 surface-elev rounded-xl p-1.5 z-50 shadow-2xl"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
          >
            <button role="menuitem" data-testid="share-link" onClick={shareLink} className="w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 hover:bg-white/5 transition-colors">
              <Link2 className="h-4 w-4 text-[color:var(--lime)]" /> Share link
            </button>
            <button role="menuitem" data-testid="share-image" onClick={shareImage} className="w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 hover:bg-white/5 transition-colors">
              <ImageIcon className="h-4 w-4 text-[color:var(--lime)]" /> Share scorecard image
            </button>
            <a role="menuitem" data-testid="share-twitter" href={tweetUrl} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 hover:bg-white/5 transition-colors">
              <span className="h-4 w-4 text-[color:var(--lime)] font-bold text-center leading-4">𝕏</span> Post on X
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareMenu;
